import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Tooltip,
  capitalize
} from '@mui/material';
import { Check, Eye, SquarePen, Trash2, X } from 'lucide-react';
import TableSkeleton from 'utils/TableSkeleton';
import styles from '../../../SuperAdminDashboard/Complaint_Management/complain.module.css';
import styles2 from './GatePassRequestTable.module.css';
import { Download } from 'lucide-react';
import ViewPassRequest from './ViewPassRequest';
import ApproveLeaveModal from './ApproveLeaveModal';
import DeleteLeaveModal from './DeleteLeaveModal';
import RejectLeaveRequest from './RejectLeaveRequest';
import { safeToAmPm } from 'views/StudentGatePass/components/GatePassRequestTable';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import axios from 'axios';
import AdminEditGatePassModal from './AdminEditGatePassModal';

function formatDate2(dateStr) {
  if (!dateStr) return 'Not Available (N/A)';
  const [day, month, year] = dateStr.split('/').map(Number);
  const date = new Date(year, month - 1, day);
  return date
    .toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
    .replace(/ /g, '-');
}

function formatTimeRange(timeRange) {
  if (!timeRange) return 'Not Available (N/A)';
  const [start, end] = timeRange.split('-').map((t) => t.trim());
  const formatTime = (timeStr) => {
    const [hour, minute] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hour, minute);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  return `${formatTime(start)} - ${formatTime(end)}`;
}

export function formatToShortReadableDate(dateStr) {
  try {
    if (!dateStr || typeof dateStr !== 'string') return 'Not Available (N/A)';

    const date = new Date(dateStr);

    if (isNaN(date.getTime())) return 'Not Available (N/A)'; // invalid date

    const day = String(date.getUTCDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getUTCMonth()];
    const year = date.getUTCFullYear();

    return `${day} ${month} ${year}`;
  } catch (err) {
    return 'Not Available (N/A)';
  }
}

export const getStatusColor = (status) => {
  switch (status.toLowerCase()) {
    case 'pending':
      return { backgroundColor: '#FEF9C3', color: '#CA8A04' };
    case 'rejected':
      return { backgroundColor: '#FEE2E2', color: '#DC2626' };
    case 'in progress':
      return { backgroundColor: '#F3E8FF', color: '#9333EA' };
    case 'approved':
      return { backgroundColor: '#DCFCE7', color: '#16A34A' };
    default:
      return { backgroundColor: 'white', color: 'white' };
  }
};

export const getRequestTypeColor = (type) => {
  switch (type.toLowerCase()) {
    case 'late entry':
      return { backgroundColor: '#DBEAFE', color: '#2563EB' };
    case 'early departure':
      return { backgroundColor: '#FBCFE8', color: '#BE185D' };
    default:
      return { backgroundColor: '#F3E8FF', color: '#9C27B0' };
  }
};

const GatePassRequestTable = ({
  data = [],
  loading = false,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  pendingCount,
  refreshTable
}) => {
  const [showPassRequestModal, setShowViewPassRequestModal] = useState(false);
  const [showApproveLeaveModal, setShowApproveLeaveModal] = useState(false);
  const [showRejectLeaveModal, setShowRejectLeaveModal] = useState(false);

  const [showDeleteLeaveModal, setShowDeleteLeaveModal] = useState(false);

  // for edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const isActionDisabled = (status) => {
    if (!status) return false;
    const s = status.toLowerCase();
    return s === 'approved' || s === 'rejected';
  };

  const handleDeleteGatePass = async (id) => {
    try {
      const token = Cookies.get('Token');

      const res = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/gate_pass/delete/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const backendMessage = res?.data?.message || 'Gate pass deleted';

      // backend can send 200 but success=false
      if (res?.data?.success === false) {
        toast.error(backendMessage);
        return;
      }

      toast.success(backendMessage);
      setShowDeleteLeaveModal(false);

      setSelectedRequest(null);
      refreshTable();
    } catch (error) {
      const fallbackMessage = 'Failed to delete gate pass';

      const backendErrorMessage = error?.response?.data?.message || fallbackMessage;

      toast.error(backendErrorMessage);
    }
  };

  // useEffect(() => {
  //   setLoading(true);
  //   const timer = setTimeout(() => setLoading(false), 800);
  //   return () => clearTimeout(timer);
  // }, [data]);

  // const filteredRequests = Array.isArray(data) ? data : [];
  const filteredRequests = Array.isArray(data) ? data : [];
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const currentPageData = filteredRequests;

  // const currentPageData = filteredRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    if (onRowsPerPageChange) onRowsPerPageChange(newRowsPerPage);
    if (onPageChange) onPageChange(0); // reset to first page
  };

  // Actions menu handlers
  const handleMenuOpen = (event, req) => {
    setAnchorEl(event.currentTarget);
    setSelectedRequest(req);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewDetails = () => {
    setShowViewPassRequestModal(true);
    handleMenuClose(); // Close menu, but keep selectedRequest for modal
  };

  return (
    <Box className={styles2.wrapper}>
      <Box className={styles.container}>
        {/* Header */}
        <Box className={`${styles.contentHeader} ${styles2.exportBtnContainer}`}>
          <Typography className={styles.contentTitle}>Gate Pass Request ({pendingCount})</Typography>
          {/* <button className={styles2.exportBtn}>
            <Download width={16} height={16} />
            <span>Export</span>
          </button> */}
        </Box>

        {/* Table */}
          {/* Pagination */}
      <Box style={{ padding: '16px 0px' }} className={styles.paginationContainer}>
        <Box className={styles.paginationInfo}>
          <Typography variant="body2" className={styles.paginationText}>
            Showing {currentPageData.length === 0 ? 0 : page * rowsPerPage + 1}-
            {Math.min(page * rowsPerPage + currentPageData.length, totalCount)} of {totalCount}
          </Typography>
        </Box>

        <Box className={styles.paginationControls}>
          <FormControl size="small" className={styles.rowsPerPage}>
            <InputLabel>Rows per page</InputLabel>
            <Select value={rowsPerPage} label="Rows per page" onChange={(e) => onRowsPerPageChange(Number(e.target.value))}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="body2" className={styles.pageInfo}>
            Page {totalPages === 0 ? 0 : page + 1} of {totalPages || 1}
          </Typography>

          <Box className={styles.navigationButtons}>
            <IconButton onClick={() => onPageChange(0)} className={styles2.paginationBtn} disabled={page === 0} aria-label="First page">
              <img src="/chevrons-left.svg" alt="first page" />
            </IconButton>
            <IconButton
              onClick={() => onPageChange(page - 1)}
              className={styles2.paginationBtn}
              disabled={page === 0}
              aria-label="Previous page"
            >
              <img src="/chevron-left.svg" alt="previous page" />
            </IconButton>
            <IconButton
              onClick={() => onPageChange(page + 1)}
              className={styles2.paginationBtn}
              disabled={page >= totalPages - 1}
              aria-label="Next page"
            >
              <img src="/chevron-right.svg" alt="next page" />
            </IconButton>
            <IconButton
              onClick={() => onPageChange(totalPages - 1)}
              className={styles2.paginationBtn}
              disabled={page >= totalPages - 1}
              aria-label="Last page"
            >
              <img src="/chevrons-right.svg" alt="last page" />
            </IconButton>
          </Box>
        </Box>
      </Box>
      
        <TableContainer component={Paper} className={styles.tableContainer}>
          {loading ? (
            <TableSkeleton />
          ) : (
            <Table>
              <TableHead>
                <TableRow className={styles.tableHeaderRow}>
                  <TableCell className={styles.tableHeaderCell}>Request ID</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Student Name</TableCell>
                  <TableCell className={styles.tableHeaderCell}>UID</TableCell>
                  {/* <TableCell className={styles.tableHeaderCell}>Category</TableCell> */}
                  <TableCell className={styles.tableHeaderCell}>Request Type</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Duration</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Reason</TableCell>
                  {/* <TableCell className={styles.tableHeaderCell}>Academic Status</TableCell> */}
                  {/* <TableCell className={styles.tableHeaderCell}>Hostel Status</TableCell> */}
                  <TableCell className={styles.tableHeaderCell}>Status</TableCell>
                  <TableCell className={styles.tableHeaderCell}>Request Date</TableCell>
                  <TableCell className={styles.tableHeaderCell}></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} style={{ textAlign: 'center', padding: '10px' }}>
                      No Requests Found
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((req) => (
                    <TableRow key={req._id} className={styles.tableRow}>
                      <TableCell className={styles.tableCell}>{req.requestId || 'Not Available (N/A)'}</TableCell>
                      <TableCell className={styles.tableCell}>{req.student?.name || 'Not Available (N/A)'}</TableCell>
                      <TableCell className={styles.tableCell}>{req.student?.UID ? `${req.student.UID}` : 'Not Available (N/A)'}</TableCell>
                      {/* <TableCell className={styles.tableCell}>{capitalize(req?.student?.category) || 'Not Available (N/A)'}</TableCell> */}
                      <TableCell className={styles.tableCell}>
                        {req.requestType ? (
                          <Chip
                            label={req.requestType}
                            style={{
                              ...getRequestTypeColor(req.requestType),
                              fontWeight: 600,
                              letterSpacing: '0.5px',
                              padding: '4px 6px',
                              fontSize: 12
                            }}
                            size="small"
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        {/* Date */}
                        <div style={{ color: '#1F2937', fontWeight: 400, fontSize: 14 }}>
                          {req?.fromDate ? formatToShortReadableDate(req?.fromDate) : 'Not Available (N/A)'}
                        </div>

                        {/* Time */}
                        {(req.requestType === 'Late Entry' || req.requestType === 'Early Departure') && req?.toTime && (
                          <div style={{ color: '#9CA3AF', fontWeight: 500, fontSize: 12 }}>
                            {req.toTime
                              ? `${req.requestType === 'Late Entry' ? `Arrival` : `Departure`} Time ${safeToAmPm(req.toTime)}`
                              : 'Not Available (N/A)'}
                          </div>
                        )}

                        {/* To Date */}
                        {req.requestType === 'Leave' && req?.toDate && (
                          <div style={{ color: '#9CA3AF', fontWeight: 500, fontSize: 12 }}>
                            {req.toDate ? `to ${formatToShortReadableDate(req.toDate)}` : 'Not Available (N/A)'}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        {req.reason ? (req.reason.length > 35 ? req.reason.substring(0, 35) + '...' : req.reason) : 'Not Available (N/A)'}
                      </TableCell>
                      {/* <TableCell className={styles.tableCell}>
                        {req.academicStatus && req.academicStatus !== 'Not Available (N/A)' ? (
                          <Chip
                            label={req.academicStatus}
                            style={{ ...getStatusColor(req.academicStatus), fontWeight: 600, letterSpacing: '0.5px' }}
                            size="small"
                          />
                        ) : (
                          'Not Available (N/A)'
                        )}
                      </TableCell> */}
                      {/* <TableCell className={styles.tableCell}>
                        {req.hostelStatus && req.hostelStatus !== 'Not Available (N/A)' ? (
                          <Chip
                            label={req.hostelStatus}
                            style={{ ...getStatusColor(req.hostelStatus), fontWeight: 600, letterSpacing: '0.5px' }}
                            size="small"
                          />
                        ) : (
                          'Not Available (N/A)'
                        )}
                      </TableCell> */}
                      <TableCell className={styles.tableCell}>
                        <Chip
                          label={req.status}
                          style={{ ...getStatusColor(req.status), fontWeight: 600, letterSpacing: '0.5px' }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell style={{ color: '#9CA3AF' }} className={styles.tableCell}>
                        {req.submittedAt
                          ? new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(
                              new Date(req.submittedAt)
                            )
                          : 'Not Available (N/A)'}
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: '4px'
                          }}
                        >
                          {/* View Details */}

                          <Tooltip title="View Request Details" arrow placement="top">
                            <IconButton
                              onClick={() => {
                                setSelectedRequest(req);
                                setShowViewPassRequestModal(true); // ONLY here
                                setShowApproveLeaveModal(false);
                                setShowRejectLeaveModal(false);
                              }}
                              sx={{
                                border: '1px solid #E5E7EB',
                                width: 24,
                                height: 24,
                                borderRadius: 1, // 4px
                                padding: 0,
                                animationTimingFunction: 'ease-out',
                                animationDuration: '300ms'
                              }}
                            >
                              <Eye size={16} color="#1f68d7" />
                            </IconButton>
                          </Tooltip>


                              {req?.requestType?.toLowerCase() == "leave" && req?.status == "Approved" &&
                          <Tooltip title="View Edit Details" arrow placement="top">
                            <IconButton
                              onClick={() => {
                                setSelectedRequest(req);
                                setEditModalOpen(true); // ONLY here
                                setShowApproveLeaveModal(false);
                                setShowRejectLeaveModal(false);
                              }}
                              sx={{
                                border: '1px solid #E5E7EB',
                                width: 24,
                                height: 24,
                                borderRadius: 1, // 4px
                                padding: 0,
                                animationTimingFunction: 'ease-out',
                                animationDuration: '300ms'
                              }}
                            >
                              <SquarePen size={16} color="#2ECC71" />
                            </IconButton>
                          </Tooltip>
                              }

                          <Tooltip title="Approve Request" arrow placement="top">
                            <IconButton
                              disabled={isActionDisabled(req?.status)}
                              onClick={() => {
                                if (isActionDisabled(req?.status)) {
                                  return;
                                }

                                setSelectedRequest(req);
                                setShowApproveLeaveModal(true);
                                setShowViewPassRequestModal(false); // hide View modal if it was open
                                setShowRejectLeaveModal(false);
                              }}
                              sx={{
                                border: '1px solid #E5E7EB',
                                width: 24,
                                height: 24,
                                borderRadius: 1,
                                padding: 0,
                                animationTimingFunction: 'ease-out',
                                animationDuration: '300ms',
                                '&:disabled': {
                                  cursor: 'not-allowed',
                                  opacity: 0.5
                                }
                              }}
                            >
                              <Check size={16} color="#30a44a" />
                            </IconButton>
                          </Tooltip>

                          {/* Approve / Tick */}
                          <Tooltip title="Reject Request" arrow placement="top">
                            <IconButton
                              disabled={isActionDisabled(req?.status)}
                              onClick={() => {
                                if (isActionDisabled(req?.status)) {
                                  return;
                                }
                                setSelectedRequest(req);
                                setShowRejectLeaveModal(true);
                                setShowViewPassRequestModal(false); // hide View modal if it was open
                                setShowApproveLeaveModal(false);
                              }}
                              sx={{
                                border: '1px solid #E5E7EB',
                                width: 24,
                                height: 24,
                                borderRadius: 1,
                                padding: 0,

                                '&:disabled': {
                                  cursor: 'not-allowed',
                                  opacity: 0.5
                                }
                              }}
                            >
                              <X size={16} color="#b91c1c" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete Request" arrow placement="top">
                            {' '}
                            <IconButton
                              onClick={() => {
                                setSelectedRequest(req);
                                setShowDeleteLeaveModal(true);
                              }}
                              sx={{
                                border: '1px solid #E5E7EB',
                                width: 24,
                                height: 24,
                                borderRadius: 1,
                                padding: 0,
                                animationTimingFunction: 'ease-out',
                                animationDuration: '300ms'
                              }}
                            >
                              <Trash2 size={16} color="#3B3B3B" />
                            </IconButton>
                          </Tooltip>
                          {/* Delete / Bin */}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

                  {/* Pagination */}
      <Box style={{ padding: '16px 0px' }} className={styles.paginationContainer}>
        <Box className={styles.paginationInfo}>
          <Typography variant="body2" className={styles.paginationText}>
            Showing {currentPageData.length === 0 ? 0 : page * rowsPerPage + 1}-
            {Math.min(page * rowsPerPage + currentPageData.length, totalCount)} of {totalCount}
          </Typography>
        </Box>

        <Box className={styles.paginationControls}>
          <FormControl size="small" className={styles.rowsPerPage}>
            <InputLabel>Rows per page</InputLabel>
            <Select value={rowsPerPage} label="Rows per page" onChange={(e) => onRowsPerPageChange(Number(e.target.value))}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="body2" className={styles.pageInfo}>
            Page {totalPages === 0 ? 0 : page + 1} of {totalPages || 1}
          </Typography>

          <Box className={styles.navigationButtons}>
            <IconButton onClick={() => onPageChange(0)} className={styles2.paginationBtn} disabled={page === 0} aria-label="First page">
              <img src="/chevrons-left.svg" alt="first page" />
            </IconButton>
            <IconButton
              onClick={() => onPageChange(page - 1)}
              className={styles2.paginationBtn}
              disabled={page === 0}
              aria-label="Previous page"
            >
              <img src="/chevron-left.svg" alt="previous page" />
            </IconButton>
            <IconButton
              onClick={() => onPageChange(page + 1)}
              className={styles2.paginationBtn}
              disabled={page >= totalPages - 1}
              aria-label="Next page"
            >
              <img src="/chevron-right.svg" alt="next page" />
            </IconButton>
            <IconButton
              onClick={() => onPageChange(totalPages - 1)}
              className={styles2.paginationBtn}
              disabled={page >= totalPages - 1}
              aria-label="Last page"
            >
              <img src="/chevrons-right.svg" alt="last page" />
            </IconButton>
          </Box>
        </Box>
      </Box>

        {/* Actions Menu */}
        {/* <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={handleViewDetails}>
            <Eye size={16} style={{ marginRight: 6 }} /> View Details
          </MenuItem>
          <MenuItem
            onClick={() => {
            }}
          >
            <X size={16} style={{ marginRight: 6 }} /> Reject Request
          </MenuItem>
        </Menu> */}
      </Box>
    

      {/* View Pass Request Modal */}
      <ViewPassRequest
        open={showPassRequestModal}
        onClose={() => {
          setShowViewPassRequestModal(false);
          console.error(selectedRequest);
          setSelectedRequest(null);
        }}
        request={selectedRequest}
        onApprove={() => {
          setShowViewPassRequestModal(false);
          setShowApproveLeaveModal(true);
        }}
        onReject={() => {
          setShowViewPassRequestModal(false);
          setShowRejectLeaveModal(true);
        }}
      />
      <ApproveLeaveModal
        open={showApproveLeaveModal}
        onClose={() => {
          setShowApproveLeaveModal(false);
          setSelectedRequest(null); // clear selected request
          refreshTable();
        }}
        onConfirm={() => {
          // TODO: handle approval API call here using selectedRequest
          console.log('Approved request:', selectedRequest);
          setShowApproveLeaveModal(false);
          setSelectedRequest(null);
          refreshTable();
        }}
        request={selectedRequest}
      />
      <DeleteLeaveModal
        open={showDeleteLeaveModal}
        onClose={() => {
          setShowDeleteLeaveModal(false);
          setSelectedRequest(null); // clear selected request
        }}
        onConfirm={(id) => {
          handleDeleteGatePass(id);
        }}
        request={selectedRequest}
      />
      <RejectLeaveRequest
        open={showRejectLeaveModal}
        onClose={() => {
          setShowRejectLeaveModal(false);
          setSelectedRequest(null);
        }}
        onConfirm={() => refreshTable()}
        request={selectedRequest} // ✅ This is the full row object
        onReject={(remark) => {
          // TODO: call reject API here with selectedRequest + remark
          setShowRejectLeaveModal(false);
          setSelectedRequest(null);
        }}
      />

     <AdminEditGatePassModal
  open={editModalOpen}
  onClose={() => {
    setEditModalOpen(false);
    setSelectedRequest(null); // consistency ke liye baaki modals jaisa
  }}
  selectedData={selectedRequest}
  fetchRequests={refreshTable}   // ✅ sahi prop naam
/>
    </Box>
  );
};

export default GatePassRequestTable;
